// src/screens/SessionHistory.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Title from "../components/Title";
import TabBar from "../components/TabBar";
import SessionItem from "../components/SessionItem";
import { getUserSessions } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

export default function SessionHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Initialize activeTab from navigation state if available
  const [activeTab, setActiveTab] = useState(() => {
    console.log('SessionHistory - Initial state check:', location.state);
    return location.state?.activeTab || 0;
  }); // 0: Upcoming, 1: Completed

  // Real session data state
  const [sessionsData, setSessionsData] = useState({
    upcomingSessions: [],
    completedSessions: [],
    totalSessions: 0,
    sessionsLast30Days: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Handle activeTab from navigation state changes
  useEffect(() => {
    console.log('SessionHistory - Location state:', location.state);
    if (location.state?.activeTab !== undefined) {
      console.log('SessionHistory - Setting activeTab to:', location.state.activeTab);
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  // Fetch user sessions on component mount
  useEffect(() => {
    const fetchUserSessions = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const sessionsResponse = await getUserSessions(user.id);
        console.log('Sessions API response:', sessionsResponse);

        // Format sessions for display - data is already formatted by the API
        const formatUpcomingSession = (session) => {
          console.log('Formatting upcoming session:', session);

          // Preserve all useful fields from the API so detail page has full data
          return {
            id: session._id || 'unknown',
            bookingId: session.bookingId || session._id,
            trainerId: session.trainerId || null,
            trainerPhoneNumber: session.trainerPhoneNumber || '',
            trainerWhatsappNumber: session.trainerWhatsappNumber || '',
            trainerCreditsPerHour: session.trainerCreditsPerHour || null,
            trainerProfilePic: session.trainerProfilePic || null,
            trainerAge: session.trainerAge || null,
            trainerSex: session.trainerSex || null,
            date: session.date || 'Unknown Date',
            time: session.time || 'Unknown Time',
            trainer: session.trainer || 'Trainer',
            sessionIn: session.sessionIn || '',
            status: session.status || 'unknown',
            startDateTime: session.startDateTime,
            endDateTime: session.endDateTime,
            location: session.location,
            address: session.address,
            notes: session.notes,
          };
        };

        const upcomingSessions = (sessionsResponse.upcomingSessions || []).map(formatUpcomingSession);

        // Filter completed sessions using the same logic as Home.tsx
        const upcomingSessionIds = new Set(sessionsResponse.upcomingSessions?.map((s) => s._id) || []);
        const completedSessions = (sessionsResponse.sessions || [])
          .filter((session) => {
            if (session.status === 'cancelled') {
              return false;
            }
            // Check if session is not in upcoming sessions
            const isNotUpcoming = !upcomingSessionIds.has(session._id);

            // Also check if the session date is in the past
            const sessionDate = new Date(session.startDateTime);
            const now = new Date();
            const isPastDate = sessionDate < now;

            return isNotUpcoming || isPastDate;
          })
          .map((session) => {
            // Format the session data to match the expected structure
            const sessionDate = new Date(session.startDateTime);
            const dateStr = sessionDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            }).toUpperCase();

            // Use endDateTime from session if available, otherwise calculate
            const endDate = session.endDateTime ? new Date(session.endDateTime) : new Date(sessionDate.getTime() + 2 * 60 * 60 * 1000);

            // Format times - dates are stored in UTC, toLocaleTimeString automatically converts to local
            const startTime = sessionDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });

            const endTimeStr = endDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });

            return {
              id: session._id,
              bookingId: session.bookingId || session._id,
              trainerId: session.trainerId || null,
              trainerPhoneNumber: session.trainerPhoneNumber || '',
              trainerWhatsappNumber: session.trainerWhatsappNumber || '',
              trainerCreditsPerHour: session.trainerCreditsPerHour || null,
              trainerProfilePic: session.trainerProfilePic || null,
              trainerAge: session.trainerAge || null,
              trainerSex: session.trainerSex || null,
              date: dateStr,
              time: `${startTime} - ${endTimeStr}`,
              trainer: session.trainerName,
              sessionIn: '',
              status: 'completed',
              startDateTime: session.startDateTime,
              endDateTime: session.endDateTime,
              location: session.location,
              address: session.address,
              notes: session.notes,
            };
          });

        console.log('Formatted sessions:', { upcomingSessions, completedSessions });

        setSessionsData({
          upcomingSessions,
          completedSessions,
          totalSessions: sessionsResponse.totalSessions || 0,
          sessionsLast30Days: sessionsResponse.sessionsLast30Days || 0
        });

      } catch (error) {
        console.error('Error fetching user sessions:', error);
        setError('Failed to load session history');
      } finally {
        setLoading(false);
      }
    };

    fetchUserSessions();
  }, [user?.id]);

  return (
    <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="mx-auto w-full min-h-full flex flex-col">
        {/* Top nav */}
        <Navbar onBack={() => navigate(-1)} />

        {/* Title */}
        <section className="px-5 pt-2">
          <Title text="// Session History" />
        </section>

        {/* Tabs */}
        <TabBar tabs={["UPCOMING", "COMPLETED"]} active={activeTab} onChange={setActiveTab} />

        {/* List */}
        <main className="px-5 pt-4 pb-10">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D62422] mx-auto mb-4"></div>
                <p className="text-gray-600">Loading sessions...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="text-[#D62422] font-semibold"
              >
                Try Again
              </button>
            </div>
          ) : activeTab === 0 ? (
            <div className="space-y-4">
              {sessionsData.upcomingSessions.length > 0 ? (
                sessionsData.upcomingSessions.map((s, i) => (
                  <div key={s.id || `${s.date}-${s.trainer}-${i}`} className="w-full">
                    <SessionItem
                      date={s.date}
                      time={s.time}
                      trainer={s.trainer}
                      sessionIn={s.sessionIn}
                      isUpcoming={true}
                      showFooter={i === 0} // red countdown bar on the first card
                      className="mx-0"
                      fullWidth={true}
                      onClick={() => navigate(`/student/session-details`, { state: { session: s } })}
                    />
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No upcoming sessions</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {sessionsData.completedSessions.length > 0 ? (
                sessionsData.completedSessions.map((s, i) => (
                  <div key={s.id || `${s.date}-${s.trainer}-${i}`} className="w-full">
                    <SessionItem
                      date={s.date}
                      time={s.time}
                      trainer={s.trainer}
                      sessionIn=""        // keep prop for type safety if required
                      isUpcoming={false}
                      showFooter={false}
                      fullWidth={true}
                      className="mx-0"
                      onClick={() => navigate(`/student/session-details`, { state: { session: s } })}
                    />
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No completed sessions</p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
